// 「全部任务」筛选 ↔ URL 查询串、「任务统计」时间范围（新加坡日期）、柱状图横轴抽稀：纯函数，便于单测
import type { Priority, TaskStatus } from "@csp/shared";

const SGT_MS = 8 * 3600_000;
const DAY_MS = 86_400_000;

export type Granularity = "day" | "week" | "month";
export type SortKey = "updatedAt" | "createdAt";
export const PAGE_SIZES = [20, 50, 100] as const;

/** 「全部任务」的全部筛选条件（空数组 / 空字符串 = 不限） */
export interface SearchFilters {
  q: string;
  status: TaskStatus[];
  typeId: string[];
  createdBy: string[];
  priority: Priority[];
  from: string;
  to: string;
  sort: SortKey;
  page: number;
  pageSize: number;
}

export const EMPTY_FILTERS: SearchFilters = {
  q: "",
  status: [],
  typeId: [],
  createdBy: [],
  priority: [],
  from: "",
  to: "",
  sort: "updatedAt",
  page: 1,
  pageSize: 20,
};

const YMD = /^\d{4}-\d{2}-\d{2}$/;
const csv = (s: string | null) => (s ?? "").split(",").map((x) => x.trim()).filter(Boolean);
const ymdOr = (s: string | null) => (s && YMD.test(s) ? s : "");

/** URL → 筛选条件（非法值丢掉、用默认值） */
export function parseFilters(p: URLSearchParams): SearchFilters {
  const page = Number(p.get("page"));
  const size = Number(p.get("pageSize"));
  return {
    q: (p.get("q") ?? "").trim(),
    status: csv(p.get("status")) as TaskStatus[],
    typeId: csv(p.get("typeId")),
    createdBy: csv(p.get("createdBy")),
    priority: csv(p.get("priority")) as Priority[],
    from: ymdOr(p.get("from")),
    to: ymdOr(p.get("to")),
    sort: p.get("sort") === "createdAt" ? "createdAt" : "updatedAt",
    page: Number.isInteger(page) && page >= 1 ? page : 1,
    pageSize: (PAGE_SIZES as readonly number[]).includes(size) ? size : 20,
  };
}

/** 筛选条件 → 查询串（默认值不写，URL 短一些）；页面 URL 和后端请求共用 */
export function filtersToParams(f: SearchFilters, forApi = false): URLSearchParams {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  if (f.status.length) p.set("status", f.status.join(","));
  if (f.typeId.length) p.set("typeId", f.typeId.join(","));
  if (f.createdBy.length) p.set("createdBy", f.createdBy.join(","));
  if (f.priority.length) p.set("priority", f.priority.join(","));
  if (f.from) p.set("from", f.from);
  if (f.to) p.set("to", f.to);
  if (forApi || f.sort !== "updatedAt") p.set("sort", f.sort);
  if (forApi || f.page !== 1) p.set("page", String(f.page));
  if (forApi || f.pageSize !== 20) p.set("pageSize", String(f.pageSize));
  return p;
}

/** 用了几项筛选（不算排序 / 分页） */
export function activeFilterCount(f: SearchFilters): number {
  return [f.q, f.status.length, f.typeId.length, f.createdBy.length, f.priority.length, f.from || f.to].filter(Boolean).length;
}

export function totalPages(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

// ---------- 新加坡日期 ----------
/** 时间 → 新加坡日期 YYYY-MM-DD */
export function sgToday(now: Date = new Date()): string {
  return new Date(now.getTime() + SGT_MS).toISOString().slice(0, 10);
}
export function addDays(ymd: string, n: number): string {
  return new Date(Date.parse(`${ymd}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
}
/** 所在周的周一 */
export function mondayOf(ymd: string): string {
  const wd = (new Date(`${ymd}T00:00:00Z`).getUTCDay() + 6) % 7;
  return addDays(ymd, -wd);
}
/** n 个月前那个月的 1 号（n=0 是本月 1 号） */
export function monthStartBefore(ymd: string, n: number): string {
  const [y, m] = ymd.split("-").map(Number) as [number, number];
  const idx = y * 12 + (m - 1) - n;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, "0")}-01`;
}

/** 快捷范围：数字 = 最近 N 段（含今天所在的那一段） */
export const RANGE_PRESETS: Record<Granularity, { n: number; label: string }[]> = {
  day: [
    { n: 7, label: "近 7 天" },
    { n: 30, label: "近 30 天" },
    { n: 90, label: "近 90 天" },
  ],
  week: [
    { n: 4, label: "近 4 周" },
    { n: 12, label: "近 12 周" },
    { n: 26, label: "近 26 周" },
  ],
  month: [
    { n: 6, label: "近 6 个月" },
    { n: 12, label: "近 12 个月" },
    { n: 24, label: "近 24 个月" },
  ],
};
export const DEFAULT_PRESET: Record<Granularity, number> = { day: 30, week: 12, month: 12 };

/** 最近 N 段的起止日期（与后端默认范围同一算法） */
export function presetRange(g: Granularity, n: number, today: string): { from: string; to: string } {
  if (g === "day") return { from: addDays(today, -(n - 1)), to: today };
  if (g === "week") return { from: addDays(mondayOf(today), -7 * (n - 1)), to: today };
  return { from: monthStartBefore(today, n - 1), to: today };
}

/** 统计页的 URL 状态：g + range（快捷 N）或 from/to（自定义）+ typeId + q */
export interface StatsFilters {
  g: Granularity;
  /** 快捷范围 N；null = 自定义 */
  preset: number | null;
  from: string;
  to: string;
  typeId: string[];
  q: string;
}

export function parseStatsFilters(p: URLSearchParams, today: string): StatsFilters {
  const gRaw = p.get("g");
  const g: Granularity = gRaw === "week" || gRaw === "month" ? gRaw : "day";
  const from = ymdOr(p.get("from"));
  const to = ymdOr(p.get("to"));
  const base = { g, typeId: csv(p.get("typeId")), q: (p.get("q") ?? "").trim() };
  if (from && to) return { ...base, preset: null, from, to };
  const n = Number(p.get("range"));
  const preset = RANGE_PRESETS[g].some((x) => x.n === n) ? n : DEFAULT_PRESET[g];
  return { ...base, preset, ...presetRange(g, preset, today) };
}

export function statsFiltersToParams(f: StatsFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (f.g !== "day") p.set("g", f.g);
  if (f.preset === null) {
    p.set("from", f.from);
    p.set("to", f.to);
  } else if (f.preset !== DEFAULT_PRESET[f.g]) p.set("range", String(f.preset));
  if (f.typeId.length) p.set("typeId", f.typeId.join(","));
  if (f.q) p.set("q", f.q);
  return p;
}

/** 横轴标签过密时每隔几个显示一个：每个标签至少要 minPx 宽 */
export function labelStep(count: number, plotWidth: number, minPx: number): number {
  if (count <= 0) return 1;
  const per = plotWidth / count;
  return Math.max(1, Math.ceil(minPx / Math.max(1, per)));
}

/** 纵轴刻度：步长取「好看」的整数（1/2/5 × 10^k），大约 4 格；max = 步长整数倍且 ≥ v */
export function niceTicks(v: number, target = 4): { max: number; step: number } {
  if (v <= 0) return { max: 1, step: 1 };
  const raw = Math.max(1, v / target);
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = ([1, 2, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? 10 * pow);
  return { max: Math.ceil(v / step) * step, step };
}
